import React from 'react';

import { classnames } from '@lumapps/classnames';
import { SelectButton } from '@lumapps/combobox/components/SelectButton';
import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiTranslate } from '@lumapps/lumx/icons';
import { Size, Emphasis, Placement, IconButton, Button, type ButtonSize, ButtonProps } from '@lumapps/lumx/react';
import { useResponsive } from '@lumapps/responsive';
import { useQueryParams } from '@lumapps/router';
import { useTranslate, GLOBAL, getKeyForLanguage } from '@lumapps/translations';

export interface LanguageSelectButtonProps {
    /** list of languages ids ('es', 'en', 'fr') */
    languages: string[];
    /** current language */
    defaultLanguage: string;
    /** current input language value */
    inputLanguage: string;
    /** whether the Language Switcher should be displayed or not */
    isVisible?: boolean;
    /** whether the language switcher should be disabled */
    isDisabled?: boolean;
    /** custom styles passed down to the button. Useful when this component needs to use custom colors  */
    styles?: Record<string, any>;
    /** if set to true, this function will return the languages defined for the current customer.
     * By default, it returns the list of languages defined for the current instance/site */
    useCustomerLanguages?: boolean;
    /** callback to be executed when the language is changed */
    onChange: (language: string) => void;
    /** scope where this component is used */
    scope?: string;
    /** emphasis that will be applied to the button */
    emphasis?: Emphasis;
    /** size that will be applied to the button */
    size?: ButtonSize;
    /** display variant */
    variant?: 'default' | 'compact';
    /** whether the switcher should be adapted to the current screen size or not */
    shouldAdaptToScreen?: boolean;
    /** amount of margin right to be added, which may vary depending on the context */
    marginRight?: Size | null;
    /** custom class name */
    className?: string;
    placement?: Placement;
    /** Props to pass to the button */
    buttonProps?: Partial<ButtonProps>;
}

/**
 * Language switcher displayed on the top bar.
 *
 * @family Translations
 * @param LanguageSelectButtonProps
 * @returns LanguageSelectButton
 */
export const LanguageSelectButton: React.FC<LanguageSelectButtonProps> = ({
    languages,
    defaultLanguage,
    inputLanguage,
    isVisible = false,
    scope = 'language-selector',
    styles = {},
    emphasis = Emphasis.low,
    size,
    onChange,
    isDisabled = false,
    variant: propVariant,
    shouldAdaptToScreen = true,
    className,
    placement = Placement.BOTTOM_START,
    buttonProps,
}) => {
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes(scope);

    const { lang: currentUserLang = defaultLanguage } = useQueryParams();
    const currentLang = languages.includes(currentUserLang) ? currentUserLang : inputLanguage;
    const value = { key: currentLang, label: translateKey(getKeyForLanguage(currentLang)) };

    const { isMobile } = useResponsive();
    let variant = propVariant;
    if (!variant && shouldAdaptToScreen) {
        variant = isMobile ? 'compact' : 'default';
    }

    const translatedLanguages = React.useMemo(() => {
        if (!languages || languages.length <= 1) {
            return undefined;
        }
        const transLangs = languages.map((lang) => ({ key: lang, label: translateKey(getKeyForLanguage(lang)) }));

        return transLangs.sort((a, b) => a.label.localeCompare(b.label));
    }, [languages, translateKey]);

    const handleChange = React.useCallback(
        (language: { key: string; label: string }) => {
            onChange(language.key);
        },
        [onChange],
    );

    if (!isVisible || !translatedLanguages || translatedLanguages.length <= 1) {
        return null;
    }

    return (
        <SelectButton
            emphasis={emphasis}
            size={size}
            label={`${translateKey(GLOBAL.LANGUAGE_SWITCHER_ARIA_LABEL)} ${value.label}`}
            {...buttonProps}
            className={classnames(className, 'language-switcher__button')}
            {...(variant === 'compact'
                ? { as: IconButton, icon: mdiTranslate, labelDisplayMode: 'show-tooltip' }
                : { as: Button, leftIcon: mdiTranslate, labelDisplayMode: 'show-selection' })}
            isDisabled={isDisabled}
            options={translatedLanguages}
            getOptionId="key"
            getOptionName="label"
            value={value}
            onChange={handleChange}
            theme={styles.theme}
            popoverProps={{ placement }}
            renderOption={(lang) => <SelectButton.Option {...get({ element: 'language', type: lang.key })} />}
            {...get({ element: 'dropdown' })}
        />
    );
};
